import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaTimes, FaRupeeSign, FaShieldAlt, FaPlus } from "react-icons/fa";
import { getUserWallet, getUserTransactions } from "../../features/wallet/walletSlice";
import api from "../../utils/apiUtils";

const AddMoneyModal = ({ isOpen, onClose, currentBalance }) => {
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.wallet);
  const { user } = useSelector((state) => state.auth);

  const [amount, setAmount] = useState("");
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const presetAmounts = [100, 250, 500, 1000, 2000, 5000];

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => {
      console.log("Razorpay script loaded successfully");
    };
    script.onerror = () => {
      console.error("Failed to load Razorpay script");
      toast.error("Failed to load payment gateway. Please refresh the page.");
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
    setSelectedPreset(null);
  };

  const handlePresetClick = (presetAmount) => {
    setAmount(presetAmount.toString());
    setSelectedPreset(presetAmount);
  };

  const createRazorpayOrder = async () => {
    try {
      const response = await api.post("/payment/wallet/create-topup-order", {
        amount: parseInt(amount),
        currency: "INR",
      });
      return response.data;
    } catch (error) {
      console.error("Error creating order:", error);
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.message || "You don't have permission to use this feature");
      } else if (error.response?.status === 404) {
        toast.error("User account not found. Please log in with a regular user account.");
      } else {
        toast.error("Failed to create payment order");
      }
      return null;
    }
  };

  const handleAddMoney = async () => {
    const numAmount = parseInt(amount);

    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (numAmount < 100) {
      toast.error("Minimum amount is ₹100");
      return;
    }

    if (numAmount > 100000) {
      toast.error("Maximum amount is ₹100,000");
      return;
    }

    setIsProcessing(true);

    try {
      const token =
        localStorage.getItem("corporateToken") ||
        localStorage.getItem("token") ||
        localStorage.getItem("vendorToken") ||
        localStorage.getItem("driverToken") ||
        localStorage.getItem("adminToken");

      if (!token) {
        toast.error("Please log in to add money to your wallet");
        setIsProcessing(false);
        return;
      }

      const orderData = await createRazorpayOrder();
      if (!orderData || !orderData.order || !orderData.order.id) {
        setIsProcessing(false);
        return;
      }

      const options = {
        key:
          orderData.key ||
          process.env.REACT_APP_RAZORPAY_KEY ||
          process.env.REACT_APP_RAZORPAY_KEY_ID ||
          "rzp_test_PnSgiqE2elzEYx",
        amount: parseInt(amount) * 100,
        currency: "INR",
        name: "CabZigo",
        description: `Add ₹${amount} to wallet`,
        order_id: orderData.order.id,
        handler: async (response) => {
          try {
            const verifyResponse = await api.post("/payment/wallet/verify-topup", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              amount: parseInt(amount),
            });

            if (verifyResponse.data?.success) {
              toast.success(`₹${amount} added to wallet successfully!`);
              dispatch(getUserWallet());
              dispatch(getUserTransactions({ page: 1 }));
              setAmount("");
              setSelectedPreset(null);
              onClose();
            } else {
              toast.error("Payment verification failed");
            }
          } catch (error) {
            console.error("Error verifying payment:", error);
            toast.error(error.response?.data?.message || "Failed to verify payment");
          } finally {
            setIsProcessing(false);
          }
        },
        prefill: {
          name: user?.user?.name || user?.name || "Customer",
          email: user?.user?.email || user?.email || "",
          contact: user?.user?.phone || user?.phone || "",
        },
        theme: {
          color: "#E05A00",
        },
      };

      if (!window.Razorpay) {
        toast.error("Payment gateway is not loaded. Please refresh and try again.");
        setIsProcessing(false);
        return;
      }

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", function (response) {
        console.error("Payment failed:", response.error);
        toast.error(`Payment failed: ${response.error.description}`);
        setIsProcessing(false);
      });
      razorpay.open();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to process payment: " + error.message);
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-start justify-center p-3 sm:p-4 pt-20 sm:pt-24 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6 sm:p-7 relative border border-slate-100">
        {/* Header - Compact */}
        <div className="flex items-center justify-between pb-2.5 border-b border-slate-100 mb-3.5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <FaRupeeSign size={15} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 leading-tight">Add Money to Wallet</h2>
              <p className="text-[11px] text-slate-500">Fast & secure top-up via UPI, Cards, or Netbanking</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            <FaTimes size={15} />
          </button>
        </div>

        {/* Horizontal 2-Column Content Grid - Compact */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {/* LEFT COLUMN: Current Balance + Quick Presets */}
          <div className="flex flex-col gap-3">
            {/* Current Balance Box */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-3">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide block">
                Current Wallet Balance
              </span>
              <span className="text-xl sm:text-2xl font-black text-blue-700 tracking-tight block">
                ₹{Number(currentBalance || 0).toFixed(2)}
              </span>
              <span className="text-[10px] text-slate-500 block">
                Funds available immediately after payment
              </span>
            </div>

            {/* Quick Presets */}
            <div>
              <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wide block mb-1.5">
                Quick Select Amount
              </span>
              <div className="grid grid-cols-3 gap-1.5">
                {presetAmounts.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => handlePresetClick(preset)}
                    className={`py-1.5 px-2 rounded-xl font-bold text-xs transition-all border ${
                      selectedPreset === preset
                        ? "bg-emerald-500 text-white border-emerald-500 shadow-sm shadow-emerald-500/20 scale-[1.02]"
                        : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    +₹{preset}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Amount Input + Action Buttons */}
          <div className="flex flex-col gap-3">
            {/* Amount Input */}
            <div>
              <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-1">
                Enter Amount (₹)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">
                  ₹
                </span>
                <input
                  type="number"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="Enter amount (min ₹100)"
                  className="w-full pl-7 pr-3 py-2 rounded-xl border border-slate-300 text-sm font-bold text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition-all placeholder:font-normal placeholder:text-xs"
                  min="100"
                  max="100000"
                />
              </div>
              <div className="flex items-center justify-between text-[10px] text-slate-400 mt-1 font-medium">
                <span>Min: ₹100</span>
                <span>Max: ₹1,00,000</span>
              </div>
            </div>

            {/* Security Notice */}
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-2.5 text-[11px] text-slate-600 space-y-0.5">
              <div className="flex items-center gap-1 font-bold text-slate-800 text-[10px] uppercase tracking-wide">
                <FaShieldAlt className="text-emerald-500" size={10} />
                <span>100% Encrypted Payment</span>
              </div>
              <p className="text-[10px] leading-snug text-slate-500">
                Processed securely via Razorpay PCI-DSS certified gateway.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2.5 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2 px-3 rounded-xl transition text-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddMoney}
                disabled={isProcessing || isLoading || !amount}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold py-2 px-3 rounded-xl transition shadow-md shadow-emerald-500/20 text-xs disabled:bg-slate-300 disabled:from-slate-300 disabled:to-slate-300 disabled:cursor-not-allowed disabled:shadow-none"
              >
                {isProcessing || isLoading ? (
                  <span>Processing...</span>
                ) : (
                  <>
                    <FaPlus size={10} />
                    <span>Add {amount ? `₹${amount}` : "Money"}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMoneyModal;
